import moment from "moment";
import { MessageTypes } from "constants/enums";
import { formatFileSize } from "utils/fileUtils";
import {
  AlertSeverity,
  BeamAlert,
  BeamEvent,
  BeamFile,
  BeamPayment,
  BeamPerson,
  BeamSurvey,
  GpsEntry,
  MediaAiAnalysis,
  PaymentMethod,
  PaymentStatus,
  RsvpChoice,
  RsvpRespondent,
  BeamCamera,
  BeamCameraAlert,
  BeamDetection,
  BeamSensorDevice,
  BeamSensorReading,
} from "./types";

/**
 * Adapters from the page's real message stream to the expanded cards.
 *
 * The cards were written against the shapes in the client's spec; the stream
 * carries whatever the send modals happened to save years earlier. Rather than
 * rewrite nine components against nine ad-hoc payloads, the mapping lives here
 * in one place — so when the backend gains a field, exactly one file changes.
 *
 * Two rules throughout:
 *  - Never invent a value. A field the payload does not carry is left undefined
 *    or given a neutral default, never a plausible-looking number.
 *  - Never throw. These run over server data during render.
 */

type Message = any;

const byType = (messages: Message[], ...types: string[]) =>
  messages.filter((m) => types.indexOf(m?.messageType) !== -1);

/** Stream ids are numeric and reused across types; cards want a stable string. */
const keyOf = (message: Message) => String(message?.id ?? message?.createdAt);

const timeOf = (message: Message) =>
  moment(message?.createdAt ?? message?.originalCreatedAt).toISOString();

/* ---------------- Payments ---------------- */

/** AddPayModal saves a symbol; the card's formatter wants a code. */
const CURRENCY_CODES: Record<string, string> = {
  $: "USD",
  "£": "GBP",
  "€": "EUR",
};

/**
 * A pay message is a *request*. Settlement is written back onto the message
 * later, so an absent status means outstanding — not failed.
 */
const toPaymentStatus = (raw?: string): PaymentStatus => {
  if (raw === "succeeded" || raw === "settled") return "settled";
  if (raw === "failed" || raw === "canceled") return "failed";
  return "pending";
};

export const toPayments = (messages: Message[]): BeamPayment[] =>
  byType(messages, MessageTypes.Pay).map((message) => ({
    id: keyOf(message),
    timestamp: timeOf(message),
    payerName: message.senderName || "Unknown",
    payerId: String(message.senderId ?? ""),
    amount: Number(message.data?.amount ?? 0),
    currency: CURRENCY_CODES[message.data?.currency] || message.data?.currency || "USD",
    method: (message.data?.method as PaymentMethod) || "Credit Card",
    status: toPaymentStatus(message.data?.status),
    // The stream has no invoice numbering, so the message id is the reference
    // an operator can actually quote back.
    invoiceRef: message.data?.invoiceRef || `MSG-${keyOf(message)}`,
  }));

/* ---------------- Events ---------------- */

const eventLocation = (location: any): string => {
  if (!location) return "";
  if (typeof location === "string") return location;
  return location.attributes?.address || location.attributes?.title || "";
};

export const toEvents = (messages: Message[]): BeamEvent[] =>
  byType(messages, MessageTypes.Event).map((message) => {
    // AddEventModal merges its date and time pickers into one epoch on send.
    const start = message.data?.dateTime ?? message.data?.start;
    const startIso = start ? moment(start).toISOString() : timeOf(message);

    return {
      id: keyOf(message),
      title: message.data?.title || message.message || "Untitled event",
      start: startIso,
      // No duration is captured anywhere in the send flow. An hour is the
      // calendar's minimum renderable block, not a claim about the event.
      end: message.data?.end
        ? moment(message.data.end).toISOString()
        : moment(startIso).add(1, "hour").toISOString(),
      location: eventLocation(message.data?.location),
      personnel: message.data?.personnel || [],
      thumbnail: message.data?.thumbnail,
      rsvp: { yes: 0, maybe: 0, no: 0 },
    };
  });

/* ---------------- RSVP ---------------- */

/**
 * An RSVP is two message types: the `rsvp` that posed the question and the
 * `rsvp-response` replies pointing back at it through `responseTo`. The card
 * shows one question, so the newest `rsvp` wins and only its own replies count.
 *
 * The three buttons carry custom labels, so a reply is matched to yes/maybe/no
 * by which button it was — position, not wording. The original label is kept as
 * the note so nothing is lost in translation.
 */
const CHOICE_BY_INDEX: RsvpChoice[] = ["yes", "maybe", "no"];

export const toRsvp = (
  messages: Message[]
): { eventTitle: string; invited: number; respondents: RsvpRespondent[] } => {
  const questions = byType(messages, MessageTypes.Rsvp);
  const question = questions[questions.length - 1];

  if (!question) {
    return { eventTitle: "", invited: 0, respondents: [] };
  }

  /**
   * Editing a message posts a *new* one that points at the original through
   * `refMessage.pageMessageId` — so an edited RSVP has two ids in the stream,
   * and replies sent before the edit still name the old one. Matching on the
   * latest id alone dropped every one of those replies and reported a live
   * RSVP as having no responses at all.
   *
   * Collapsing each question onto the id of the message it descends from lets
   * the whole edit chain answer to one identity.
   */
  const rootOf = (message: Message) =>
    String(
      message?.refMessage?.pageMessageId ??
        message?.originalPageMessageId ??
        message?.id
    );

  const root = rootOf(question);
  const chain = questions
    .filter((candidate) => rootOf(candidate) === root)
    .map((candidate) => String(candidate.id))
    .concat(root);

  const options = [
    question.data?.options?.option1,
    question.data?.options?.option2,
    question.data?.options?.option3,
  ];

  const respondents = byType(messages, MessageTypes.RsvpResponse)
    .filter((reply) => chain.indexOf(String(reply.data?.responseTo)) !== -1)
    .map((reply) => {
      const answer = reply.data?.response;
      const index = options.findIndex((option) => option === answer);

      return {
        id: keyOf(reply),
        name: reply.senderName || "Unknown",
        // Calendar-style RSVPs answer with a timestamp rather than a button,
        // which is a commitment — the closest of the three is "yes".
        choice: index >= 0 ? CHOICE_BY_INDEX[index] : "yes",
        respondedAt: timeOf(reply),
        note: typeof answer === "string" ? answer : undefined,
      } as RsvpRespondent;
    });

  return {
    eventTitle: question.data?.rsvpTitle || question.message || "RSVP",
    // Invited count is a page-membership fact, not a message one; the caller
    // supplies it. Falling back to replies keeps the rate at 100% rather than
    // dividing by zero.
    invited: respondents.length,
    respondents,
  };
};

/* ---------------- Alerts ---------------- */

/**
 * IoTAlertModal saves alert *rules*; the backend pushes *fired* alerts. Both
 * land in this list because both are what an operator scans. Severity only
 * exists on fired alerts, so a rule is filed as info rather than guessed at.
 */
const toSeverity = (raw?: string): AlertSeverity => {
  if (raw === "critical" || raw === "warning" || raw === "info") return raw;
  return "info";
};

const ruleSummary = (data: any): string => {
  const parts: string[] = [];
  if (data?.lowerSpeedThreshold || data?.upperSpeedThreshold) {
    parts.push(
      `Speed ${data.lowerSpeedThreshold ?? "–"}–${data.upperSpeedThreshold ?? "–"} mph`
    );
  }
  if (data?.speedingTime) parts.push(`over ${data.speedingTime} min`);
  if (data?.stopTime) parts.push(`stopped ${data.stopTime} min`);
  return parts.join(" · ");
};

export const toAlerts = (messages: Message[]): BeamAlert[] =>
  byType(messages, MessageTypes.Alert, MessageTypes.AlertConfiguration).map(
    (message) => ({
      id: keyOf(message),
      severity: toSeverity(message.data?.severity),
      type: message.data?.type || message.data?.name || "Alert",
      description:
        message.data?.description ||
        ruleSummary(message.data) ||
        message.message ||
        "",
      occurredAt: message.data?.occurredAt
        ? moment(message.data.occurredAt).toISOString()
        : timeOf(message),
      artifact: message.data?.artifact,
    })
  );

/* ---------------- People ---------------- */

/**
 * People are page *members*, not messages — there is no `people` entry in the
 * MessageTypes enum and nothing in the app ever sends one.
 *
 * The member map only carries membership facts (role, pending invite). Who the
 * person actually *is* lives in the cached user directory, so this joins the
 * two the same way PeopleModal does before rendering a member row.
 */
export const toPeopleFromMembers = (
  users: any,
  allUsers?: any[]
): BeamPerson[] => {
  if (!users) return [];

  return Object.keys(users)
    // Pending invitees are not members yet; listing them as staff would
    // misrepresent who is actually on the page.
    .filter((id) => !users[id]?.pending)
    .map((id) => {
      const member = users[id] || {};
      const profile = allUsers?.find((user) => user?.id === id) || {};
      const fullName = [profile.firstName, profile.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();

      return {
        id,
        // The directory is fetched lazily and cached, so it can legitimately
        // not hold this member yet. The beamId is a real identifier the
        // operator recognises — better than the word "Unknown".
        name: fullName || profile.name || profile.beamId || member.beamId || id,
        role: member.role || "Member",
        badgeId: profile.beamId || member.beamId || "",
        phone: profile.phone || "",
        email: profile.email || "",
        avatar: profile.profileImage?.urls?.original?.url,
      };
    });
};

/* ---------------- Surveys ---------------- */

export const toSurveys = (messages: Message[]): BeamSurvey[] =>
  byType(messages, MessageTypes.Survey).map((message) => ({
    id: keyOf(message),
    title: message.data?.title || message.message || "Survey",
    format: "SURVEY",
    author: message.senderName || "Unknown",
    createdAt: timeOf(message),
    projectTag: message.data?.projectTag || "",
    // Totals live in the shared survey app, not on the message. Zero is the
    // honest placeholder; the card links out for the real numbers.
    responses: message.data?.responses ?? 0,
    completionRate: message.data?.completionRate ?? 0,
    closedAt: message.data?.closedAt,
  }));

/* ---------------- GPS ---------------- */

/**
 * Pins come from place messages and from any message carrying coordinates —
 * a GPS report references a track the map fetches separately, so it has no
 * point of its own to plot here.
 */
export const toGpsEntries = (messages: Message[]): GpsEntry[] => {
  const entries: GpsEntry[] = [];

  messages.forEach((message) => {
    const coords =
      message?.data?.coordinates ||
      message?.data?.attributes?.coordinates ||
      message?.data?.location?.coordinates;

    const lat = Number(coords?.lat ?? coords?.latitude);
    const lng = Number(coords?.lng ?? coords?.long ?? coords?.longitude);
    if (!isFinite(lat) || !isFinite(lng)) return;

    entries.push({
      id: keyOf(message),
      label:
        message.data?.attributes?.name ||
        message.data?.attributes?.address ||
        message.message ||
        "Location",
      lat,
      lng,
      speedMph: message.data?.speedMph,
      elevationFt: message.data?.elevationFt,
      capturedAt: timeOf(message),
    });
  });

  return entries;
};

/**
 * Route breadcrumbs are the same points as the pins, joined oldest-first.
 *
 * The stream arrives newest-first, so plotting it unsorted would draw the path
 * backwards — harmless for a closed loop, wrong for anything else.
 */
export const toGpsRoute = (entries: GpsEntry[]) =>
  [...entries]
    .sort(
      (a, b) =>
        new Date(a.capturedAt).getTime() - new Date(b.capturedAt).getTime()
    )
    .map((entry) => ({ lat: entry.lat, lng: entry.lng }));

/* ---------------- Files ---------------- */

/** Media that plays or displays inline belongs in the gallery, not the file list. */
const INLINE_MEDIA = ["image", "video", "audio"];

/** MediaGallery's marker for a pasted link rather than an uploaded file. */
const LINK_MIME_TYPE = "application/link";

/**
 * Documents shared on the page.
 *
 * These are ordinary `media` messages — the split is by MIME type, not by a
 * separate message type: whatever the gallery cannot show inline is a file.
 */
export const toFiles = (messages: Message[]): BeamFile[] =>
  byType(messages, MessageTypes.Media)
    .filter((message) => {
      const mime: string = message?.data?.mimeType || "";
      if (!mime || mime === LINK_MIME_TYPE) return false;
      return !INLINE_MEDIA.some((kind) => mime.indexOf(kind) !== -1);
    })
    .map((message) => {
      const name =
        message.data?.originalName || message.data?.name || message.message || "File";
      const bytes = message.data?.size ?? message.data?.contentLength;

      return {
        id: keyOf(message),
        name,
        extension: (name.split(".").pop() || "").toUpperCase(),
        // formatFileSize returns "0 bytes" for a missing count, which reads as
        // an empty file rather than an unknown one — so omit it instead.
        sizeLabel: Number.isFinite(Number(bytes))
          ? formatFileSize(Number(bytes))
          : undefined,
        // Nothing in the upload pipeline versions a file today; the card drops
        // the field rather than showing every document as "v1".
        version: undefined,
        uploader: message.senderName || "Unknown",
        uploadedAt: timeOf(message),
        url: message.data?.urls?.original?.url || "",
      };
    });

/* ---------------- AI recognition ---------------- */

/**
 * Visual and auditory recognition results for the page's media.
 *
 * There is no analysis service behind this yet. The contract is that the media
 * service writes its results onto the media object as `data.ai`, in the shape
 * `MediaAiAnalysis` describes minus the four fields derived here from the
 * message itself (mediaId, mediaName, mediaKind, thumbnail).
 *
 * Until that field exists this returns an empty list, and the view says so.
 * Emitting a "queued" card per media instead would claim work is in progress
 * that nothing is actually doing.
 */
export const toMediaAiAnalyses = (messages: Message[]): MediaAiAnalysis[] =>
  byType(messages, MessageTypes.Media)
    .filter((message) => !!message?.data?.ai)
    .map((message) => {
      const mime: string = message.data?.mimeType || "";
      const kind: MediaAiAnalysis["mediaKind"] =
        mime.indexOf("video") !== -1
          ? "video"
          : mime.indexOf("audio") !== -1
          ? "audio"
          : "image";

      return {
        ...message.data.ai,
        mediaId: keyOf(message),
        mediaName:
          message.data?.originalName || message.data?.name || "Media",
        mediaKind: message.data.ai.mediaKind || kind,
        thumbnail:
          message.data.ai.thumbnail ||
          (kind === "image" ? message.data?.urls?.original?.url : undefined),
        status: message.data.ai.status || "ready",
      };
    });

/* ---------------- Cameras (beamAI vision) ---------------- */

/**
 * `cam` messages are published by the device side, not by a modal in this app,
 * so unlike payments or events there is no single writer whose field names we
 * can rely on. Each field is read from the handful of spellings the payloads
 * in circulation actually use, and left undefined when none is present — a
 * camera tile shows what it knows rather than a plausible-looking blank.
 */
const firstOf = (...values: any[]) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

export const toCameras = (messages: Message[]): BeamCamera[] =>
  byType(messages, MessageTypes.Cam).map((message) => {
    const data = message?.data || {};
    return {
      id: keyOf(message),
      name:
        firstOf(data.name, data.cameraName, data.deviceName, message?.message) ||
        "Camera",
      location: firstOf(data.location, data.place, data.zone),
      iotTopic: data.iotTopic,
      deviceKey: firstOf(
        data.deviceKey,
        message?.deviceKey,
        data.deviceId,
        data.iotUdid
      ),
      rtspUrl: firstOf(data.rtspUrl, data.urls?.original?.url, data.url),
      streamName: firstOf(data.streamName, data.urls?.original?.name, data.name),
      snapshotUrl: firstOf(data.snapshotUrl, data.thumbnail, data.urls?.thumbnail?.url),
      addedAt: timeOf(message),
    };
  });

/**
 * Fired alerts, matched back to the camera that raised them.
 *
 * Only real identity is used for the match — device key, topic, or the camera's
 * own name appearing in the alert. An alert that matches nothing keeps
 * `cameraId` undefined rather than being attributed to the first camera in the
 * list, because a flag on the wrong feed is worse than no flag at all.
 *
 * Alert *rules* (`alert-configuration`) are deliberately excluded: a rule is a
 * setting, and raising a flag on a live camera for one would be a false alarm.
 */
export const toCameraAlerts = (
  messages: Message[],
  cameras: BeamCamera[]
): BeamCameraAlert[] =>
  byType(messages, MessageTypes.Alert).map((message) => {
    const data = message?.data || {};
    const deviceKey = firstOf(data.deviceKey, data.deviceId, data.imei);
    const topic = data.iotTopic;
    const match = cameras.find((camera) => {
      if (deviceKey && camera.deviceKey && camera.deviceKey === deviceKey)
        return true;
      if (topic && camera.iotTopic && camera.iotTopic === topic) return true;
      const named = firstOf(data.cameraName, data.source, data.deviceName);
      return Boolean(named) && named === camera.name;
    });

    return {
      id: keyOf(message),
      cameraId: match?.id,
      severity: toSeverity(data.severity),
      type: firstOf(data.type, data.name) || "Alert",
      description: firstOf(data.description, message?.message) || "",
      occurredAt: data.occurredAt
        ? moment(data.occurredAt).toISOString()
        : timeOf(message),
    };
  });

/**
 * A beamAI sample as the socket delivers it: `{ dataset: { personCount: 2, … },
 * timestamp }`. The keys are whatever the model on that edge node emits, so the
 * label is derived from the key rather than looked up in a fixed table — a new
 * class the model learns shows up by itself instead of being silently dropped.
 */
const DETECTION_LABELS: Record<string, string> = {
  personCount: "People",
  bicycleCount: "Bicycles",
  carCount: "Cars",
  busCount: "Buses",
  truckCount: "Trucks",
  motorcycleCount: "Motorcycles",
  airplaneCount: "Aircraft",
};

const labelForDetection = (key: string): string =>
  DETECTION_LABELS[key] ||
  key
    .replace(/Count$/, "")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

/**
 * `state.dashboard.rx` is written by two producers — the REST saga hands over a
 * batch, the socket hands over a single sample — so the slot legitimately holds
 * either shape. Unwrapping here means neither reader has to know which one
 * wrote last.
 */
export const latestRxSample = (rx: any): any => {
  if (!rx) return undefined;
  if (Array.isArray(rx)) return rx[0];
  if (Array.isArray(rx?.samples)) return rx.samples[0];
  return rx;
};

export const toDetections = (rx: any): BeamDetection[] => {
  const sample = latestRxSample(rx);
  const dataset = sample?.dataset;
  if (!dataset || typeof dataset !== "object") return [];
  return Object.keys(dataset)
    .filter((key) => typeof dataset[key] === "number")
    .map((key) => ({
      label: labelForDetection(key),
      count: dataset[key],
      // The current payload carries no geometry. When the inference service
      // starts sending boxes they are picked up here; until then the overlay
      // renders labels only rather than drawing a rectangle at a made-up spot.
      box: Array.isArray(dataset[`${key}Box`])
        ? (dataset[`${key}Box`] as [number, number, number, number])
        : undefined,
      confidence:
        typeof dataset[`${key}Confidence`] === "number"
          ? dataset[`${key}Confidence`]
          : undefined,
    }))
    .filter((detection) => detection.count > 0);
};

/* ---------------- beamRx sensors ---------------- */

export const toSensorDevices = (messages: Message[]): BeamSensorDevice[] =>
  byType(messages, MessageTypes.Rx).map((message) => {
    const data = message?.data || {};
    return {
      id: keyOf(message),
      name:
        firstOf(data.name, data.deviceName, message?.message) || "Sensor",
      iotTopic: data.iotTopic,
      deviceKey: firstOf(
        data.deviceKey,
        message?.deviceKey,
        data.deviceId,
        data.iotUdid
      ),
      addedAt: timeOf(message),
    };
  });

/**
 * The beamRx payload is flat and triple-keyed per metric —
 * `heartRateValue` / `heartRateUnit` / `heartRateTimestamp` — which is why
 * RxSensorTableModal had a hand-written `if` per metric. Reading the triple
 * generically means a metric the firmware adds later appears on its own.
 */
const SENSOR_LABELS: Record<string, string> = {
  heartRate: "Heart rate",
  temperature: "Body temperature",
  bodyTemp: "Body temperature",
  bloodOxygen: "Blood oxygen",
  bloodPressure: "Blood pressure",
  bloodPressureSystolic: "Blood pressure (systolic)",
  bloodPressureDiastolic: "Blood pressure (diastolic)",
  bloodPressureHigh: "Blood pressure (systolic)",
  bloodPressureLow: "Blood pressure (diastolic)",
  respiratoryRate: "Respiratory rate",
  distance: "Distance",
  sleep: "Sleep",
  steps: "Steps",
  cal: "Calories",
  calories: "Calories",
  humidity: "Humidity",
  pressure: "Pressure",
  battery: "Battery",
};

const labelForSensor = (base: string): string =>
  SENSOR_LABELS[base] ||
  base.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());

export const toSensorReadings = (sample: any): BeamSensorReading[] => {
  if (!sample || typeof sample !== "object") return [];

  const triples = Object.keys(sample)
    .filter((key) => /Value$/.test(key))
    .map((key) => {
      const base = key.replace(/Value$/, "");
      return {
        key: base,
        label: labelForSensor(base),
        value: sample[key],
        unit: sample[`${base}Unit`],
        timestamp: sample[`${base}Timestamp`],
      };
    })
    .filter((reading) => reading.value !== undefined && reading.value !== "");

  if (triples.length) return triples;

  /*
    The triple-keyed form above is what the REST batch and the wearables send.
    An append straight off the wire carries its metrics in `dataset` under bare
    names instead, with no unit beside them — the simulator in the RX Sensor
    modal publishes exactly that. Reading both means the card shows a live feed
    from either producer; the unit is simply left off when the payload has none
    rather than being guessed from the metric name.
  */
  const dataset = sample.dataset;
  if (!dataset || typeof dataset !== "object") return [];
  return Object.keys(dataset)
    .filter((key) => typeof dataset[key] === "number")
    .map((key) => ({
      key,
      label: labelForSensor(key),
      value: dataset[key],
      unit: undefined,
      timestamp: sample.timestamp,
    }));
};
